import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import './styles/student.css';
import Signup from './pages/Signup';
import Login from './pages/Login';
import Otp from './pages/Otp';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import StudentPage from './pages/StudentPage';
import GuardPage from './pages/GuardPage';
import AdminPage from './pages/AdminPage';
import HodPage from './pages/HodPage';
import DugcPage from './pages/DugcPage';
import OfficeSecretaryPage from './pages/OfficeSecretaryPage';
import HostelOfficePage from './pages/HostelOfficePage';
import FacultyPage from './pages/FacultyPage';
import DpgcPage from './pages/DpgcPage';
import DeanPage from './pages/DeanPage';
import { getUserFromToken } from './utils/auth';

// Helper function to get home route based on user role
const getHomeRoute = (role) => {
  switch (role) {
    case 'student':
      return '/student';
    case 'guard':
      return '/guard';
    case 'admin':
      return '/admin';
    case 'warden':
      return '/admin';
    case 'hod':
      return '/hod';
    case 'dugc':
      return '/dugc';
    case 'officeSecretary':
      return '/office-secretary';
    case 'hostelOffice':
      return '/hostel-office';
    case 'faculty':
      return '/faculty';
    case 'dpgc':
      return '/dpgc';
    case 'dean':
      return '/dean';
    default:
      return '/login';
  }
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const user = getUserFromToken();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // If role not allowed, send user back to login
    return <Navigate to="/login" replace />;
  }

  if (user.role === 'student' && isStandaloneMode()) {
    return <StandaloneStudentBlocked />;
  }

  return children;
};

const isStandaloneMode = () => {
  const byMediaQuery = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
  const byIosFlag = window.navigator.standalone === true;
  const byAndroidReferrer = document.referrer && document.referrer.startsWith('android-app://');
  return Boolean(byMediaQuery || byIosFlag || byAndroidReferrer);
};

const StandaloneStudentBlocked = () => {
  useEffect(() => {
    const browserUrl = `${window.location.origin}/login?source=browser`;

    // Attempt redirect out of installed/standalone context
    const timer = setTimeout(() => {
      window.location.replace(browserUrl);
    }, 1200);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      background: '#f6f8fb',
      color: '#1f2a37',
      textAlign: 'center',
      fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
    }}>
      <div style={{ maxWidth: '560px', background: '#fff', borderRadius: '12px', padding: '24px', boxShadow: '0 6px 20px rgba(0,0,0,0.08)' }}>
        <h2 style={{ marginTop: 0, marginBottom: '10px' }}>Use Browser Version Only</h2>
        <p style={{ margin: 0, lineHeight: 1.6 }}>
          Student access from installed app/shortcut is disabled.
          You are being redirected to the browser version.
        </p>
        <p style={{ marginTop: '12px', marginBottom: 0, fontSize: '14px', color: '#4b5563' }}>
          If this page stays open, please uninstall this app icon and open GoThru directly in Chrome.
        </p>
      </div>
    </div>
  );
};

// Redirect authenticated users to their home screen
const AuthRoute = ({ children }) => {
  const user = getUserFromToken();

  if (user) {
    // User is already logged in, redirect to their home
    return <Navigate to={getHomeRoute(user.role)} replace />;
  }

  return children;
};

// Root redirect component that checks auth status
const RootRedirect = () => {
  const user = getUserFromToken();
  if (user) {
    return <Navigate to={getHomeRoute(user.role)} replace />;
  }
  return <Navigate to="/login" replace />;
};

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/signup" element={<AuthRoute><Signup /></AuthRoute>} />
        <Route path="/login" element={<AuthRoute><Login /></AuthRoute>} />
        <Route path="/otp" element={<Otp />} />
        <Route path="/forgot-password" element={<AuthRoute><ForgotPassword /></AuthRoute>} />
        <Route path="/reset-password" element={<AuthRoute><ResetPassword /></AuthRoute>} />
        <Route
          path="/student/*"
          element={
            <ProtectedRoute allowedRoles={['student']}>
              <StudentPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/guard/*"
          element={
            <ProtectedRoute allowedRoles={['guard']}>
              <GuardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute allowedRoles={['admin', 'warden']}>
              <AdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/hod/*"
          element={
            <ProtectedRoute allowedRoles={['hod']}>
              <HodPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dugc/*"
          element={
            <ProtectedRoute allowedRoles={['dugc']}>
              <DugcPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/office-secretary/*"
          element={
            <ProtectedRoute allowedRoles={['officeSecretary']}>
              <OfficeSecretaryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/hostel-office/*"
          element={
            <ProtectedRoute allowedRoles={['hostelOffice']}>
              <HostelOfficePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/faculty/*"
          element={
            <ProtectedRoute allowedRoles={['faculty']}>
              <FacultyPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dpgc/*"
          element={
            <ProtectedRoute allowedRoles={['dpgc']}>
              <DpgcPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dean/*"
          element={
            <ProtectedRoute allowedRoles={['dean']}>
              <DeanPage />
            </ProtectedRoute>
          }
        />
      </Routes>
    </Router>
  );
}

export default App;
